// Epoch 35: CSR Companies Management Page
// Shows all companies assigned to the current CSR
// Date: August 17, 2025

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase/client';
import { useNavigate } from 'react-router-dom';
import { useAuthSession } from '../../hooks/useAuthSession';

interface AssignedCompany {
  company_id: string;
  company_name: string;
  user_count: number;
  campaign_count: number;
  response_count: number;
  last_campaign_date: string | null;
  created_at: string;
}

const CSRCompanies: React.FC = () => {
  const [companies, setCompanies] = useState<AssignedCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const { session } = useAuthSession();

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true);
      
      // First get companies assigned to this CSR
      const { data: companiesData, error: companiesError } = await supabase
        .from('companies')
        .select('id, name, created_at')
        .eq('assigned_csr_id', session?.user?.id);

      if (companiesError) throw companiesError;

      if (!companiesData || companiesData.length === 0) {
        setCompanies([]);
        return;
      }

      // Get counts for each company
      const companiesWithCounts = await Promise.all(
        companiesData.map(async (company) => {
          // Count users
          const { count: userCount } = await supabase
            .from('users')
            .select('*', { count: 'exact', head: true })
            .eq('company_id', company.id);

          // Count campaigns
          const { data: campaignData } = await supabase
            .from('campaigns')
            .select('id, created_at')
            .in('user_id', 
              await supabase
                .from('users')
                .select('id')
                .eq('company_id', company.id)
                .then(res => res.data?.map(u => u.id) || [])
            );

          // Count lead responses
          const { count: responseCount } = await supabase
            .from('lead_responses')
            .select('*', { count: 'exact', head: true })
            .in('campaign_id', campaignData?.map(c => c.id) || []);

          // Get last campaign date
          const lastCampaignDate = campaignData && campaignData.length > 0
            ? Math.max(...campaignData.map(c => new Date(c.created_at).getTime()))
            : null;

          return {
            company_id: company.id,
            company_name: company.name,
            user_count: userCount || 0,
            campaign_count: campaignData?.length || 0,
            response_count: responseCount || 0,
            last_campaign_date: lastCampaignDate ? new Date(lastCampaignDate).toISOString() : null,
            created_at: company.created_at
          };
        })
      );

      setCompanies(companiesWithCounts);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoading(false);
    }
  }, [session?.user?.id]);

  useEffect(() => {
    if (session?.user?.id) {
      loadCompanies();
    }
  }, [session?.user?.id, loadCompanies]);

  const filteredCompanies = companies.filter(company =>
    company.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading assigned companies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My Assigned Companies</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Manage companies and users assigned to you as a CSR
                </p>
              </div>
              <button
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="max-w-md">
            <label htmlFor="search" className="sr-only">Search companies</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                id="search"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Search companies..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">{companies.length}</div>
            <div className="text-sm text-gray-600">Total Companies</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="text-2xl font-bold text-green-600">
              {companies.reduce((sum, c) => sum + c.user_count, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Users</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="text-2xl font-bold text-purple-600">
              {companies.reduce((sum, c) => sum + c.campaign_count, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Campaigns</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
            <div className="text-2xl font-bold text-orange-600">
              {companies.reduce((sum, c) => sum + c.response_count, 0)}
            </div>
            <div className="text-sm text-gray-600">Total Responses</div>
          </div>
        </div>

        {/* Companies List */}
        {filteredCompanies.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No companies found' : 'No companies assigned'}
            </h3>
            <p className="text-gray-600">
              {searchTerm 
                ? 'Try adjusting your search criteria'
                : 'Contact your admin to get companies assigned to you'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCompanies.map((company) => (
              <div key={company.company_id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {company.company_name}
                    </h3>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Users:</span>
                      <span className="font-medium text-gray-900">{company.user_count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Campaigns:</span>
                      <span className="font-medium text-gray-900">{company.campaign_count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Lead Responses:</span>
                      <span className="font-medium text-gray-900">{company.response_count}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Last Campaign:</span>
                      <span className="font-medium text-gray-900">
                        {formatDate(company.last_campaign_date)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-6 flex space-x-3">
                    <button
                      onClick={() => navigate(`/csr-users?company=${company.company_id}`)}
                      className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      View Users
                    </button>
                    <button
                      onClick={() => navigate(`/analytics?company=${company.company_id}`)}
                      className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      Analytics
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CSRCompanies;